/**
 * Care notes — the care team's own words, stored verbatim (decision D3).
 *
 * A physio return-to-running handout carries a block of rules the volume alone cannot
 * express: "don't run more often than every other day", "advance only when you can do
 * the step without pain and swelling", "go back a level if you start to limp", "start on
 * even, soft ground". Before this they were parsed, read once to guess the sport, and
 * discarded — see docs/audits/physio-plan-import-fidelity-2026-08-14.md (F1).
 *
 * Three properties define the model, and each is a safety property, not a style choice:
 *
 *  1. VERBATIM. A note is the author's sentence. It is never paraphrased, summarized,
 *     reordered into a category, or regenerated. A plausible-sounding rule carrying a
 *     physio's authority that the physio did not write is worse than no rule at all, so
 *     the system may only ever store and display what it was given.
 *  2. ATTRIBUTED. Every note records where it came from, so the surface can say "from
 *     your physio's plan" honestly — and can never present a generic default that way.
 *  3. CONFIRMED. Nothing lands here that a human did not see first. The import review
 *     shows the exact lines and lets any of them be removed before they are stored.
 *
 * Storage mirrors raceGoal.ts / returnCriteria.ts: a typed key on the active goal's
 * existing `metadata` JSONB rather than a new column, avoiding both a migration and the
 * column-per-need anti-pattern. Goal level is also the right granularity — the care
 * envelope is about the athlete's return, not about one plan, and it is what the Track
 * surface already reads per athlete.
 *
 * NO Deno/Supabase imports → unit-tested via the `src/domain/rehab/careNotes.ts`
 * re-export (mirrors returnCriteria.ts / painSignal.ts).
 */

export const CARE_NOTES_METADATA_KEY = 'care_notes' as const;

/** Where a note came from. Only professional/document sources may be attributed. */
export type CareNoteSource = 'import' | 'professional';

export interface CareNote {
  /** The author's sentence, exactly as it was written. */
  text: string;
  source: CareNoteSource;
  /** Document it came from (file name / title), when known — provenance for the athlete. */
  document?: string | null;
}

/**
 * Bounds. These are prose entering a clinical surface, so the shape is capped rather
 * than trusted: a parser or a paste can produce arbitrary volume, and an unbounded list
 * would let the safety envelope be buried in noise or push a row past what the metadata
 * column should carry.
 */
export const MAX_CARE_NOTES = 12;
export const MAX_CARE_NOTE_LENGTH = 300;

type GoalLike = { metadata?: Record<string, unknown> | null } | null | undefined;

/** Parse one raw entry, or null when it cannot be trusted as a note. */
function parseNote(raw: unknown): CareNote | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.text !== 'string') return null;
  const text = r.text.trim();
  if (text.length === 0) return null;

  // An unknown source must NOT default to an attributable one — that is exactly how a
  // generic line would end up presented as something a clinician wrote.
  const source: CareNoteSource = r.source === 'professional' ? 'professional' : 'import';
  const document = typeof r.document === 'string' && r.document.trim().length > 0
    ? r.document.trim()
    : null;

  return { text: text.slice(0, MAX_CARE_NOTE_LENGTH), source, document };
}

/** The care team's notes on this goal, in order. Empty when none are set. */
export function getCareNotes(goal: GoalLike): CareNote[] {
  const meta = goal?.metadata;
  if (!meta || typeof meta !== 'object') return [];
  const raw = (meta as Record<string, unknown>)[CARE_NOTES_METADATA_KEY];
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const out: CareNote[] = [];
  for (const entry of raw) {
    const note = parseNote(entry);
    if (!note || seen.has(note.text)) continue;
    seen.add(note.text);
    out.push(note);
    if (out.length >= MAX_CARE_NOTES) break;
  }
  return out;
}

export function hasCareNotes(goal: GoalLike): boolean {
  return getCareNotes(goal).length > 0;
}

/** Just the sentences — what a display surface renders. */
export function careNoteTexts(notes: readonly CareNote[]): string[] {
  return notes.map((n) => n.text);
}

/**
 * Normalize a set of notes for storage: trim, drop empties, deduplicate, truncate
 * over-long lines, and cap the count. Never rewrites the wording.
 */
export function normalizeCareNotes(notes: readonly CareNote[] | null | undefined): CareNote[] {
  if (!notes) return [];
  const seen = new Set<string>();
  const out: CareNote[] = [];
  for (const raw of notes) {
    const note = parseNote(raw);
    if (!note || seen.has(note.text)) continue;
    seen.add(note.text);
    out.push(note);
    if (out.length >= MAX_CARE_NOTES) break;
  }
  return out;
}

/**
 * Merge notes into a goal's existing metadata without clobbering other keys (the write
 * path). Passing null/empty removes the key entirely.
 */
export function buildCareNotesMetadata(
  existing: Record<string, unknown> | null | undefined,
  notes: readonly CareNote[] | null | undefined,
): Record<string, unknown> {
  const base = { ...(existing ?? {}) };
  const normalized = normalizeCareNotes(notes);
  if (normalized.length === 0) {
    delete base[CARE_NOTES_METADATA_KEY];
    return base;
  }
  return { ...base, [CARE_NOTES_METADATA_KEY]: normalized };
}

/**
 * Add notes to the ones a goal already has, keeping existing entries and their order.
 *
 * Additive because an import must not silently erase what a previous document (or a
 * physio typing directly) established. De-duplicated on exact text, so re-importing the
 * same handout is idempotent rather than doubling the envelope.
 */
export function mergeCareNotes(
  existing: readonly CareNote[] | null | undefined,
  incoming: readonly CareNote[] | null | undefined,
): CareNote[] {
  return normalizeCareNotes([...(existing ?? []), ...(incoming ?? [])]);
}

/** Build notes from verbatim source lines carried by an import. */
export function careNotesFromImport(
  lines: readonly string[] | null | undefined,
  document?: string | null,
): CareNote[] {
  return normalizeCareNotes(
    (lines ?? []).map((text) => ({ text, source: 'import' as const, document: document ?? null })),
  );
}
